import React, {useState} from "react";
import {Form, Formik, FormikProps} from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import SubjectInput from "./SubjectInput";
import FileSelector from "./FileSelector";
import InvoiceModalView from "../invoice/InvoiceModalView";
import EntityId from "../../core/api/EntityId";
import InvoiceEmailModel, { defaultInvoiceEmailData, invoiceEmailSchema } from "../../core/api/invoice/InvoiceEmailModel";
import { ClientEmailSelect } from "../client/ClientEmailSelect";
import {useNotifications} from "../../hooks/NotificationProvider";
import { translations } from "../../translations/common";
import TazEditor from "./TazEditor";

const RootContainer = styled('div')({
  maxWidth: "450px",
  display: "block",
  margin: "0 auto",
});

const StyledGrid = styled(Grid)({
  "& > *": {
    width: "100%",
  },
});

const StyledDialogTitle = styled(DialogTitle)({
  textAlign: "center",
});

interface Props {
  clientId: string;
  title: string;

  close(): void;

  onSubmit(content: InvoiceEmailModel): Promise<EntityId>;

  pdfUrl(): Promise<string>;
}

const InvoiceEmailModalForm: React.FC<Props> = ({
                                                  title,
                                                  close,
                                                  onSubmit,
                                                  pdfUrl,
                                                  clientId,
                                                }) => {
  const [preview, setPreview] = useState(false);
  const notifications = useNotifications();
  return (
    <RootContainer>
      <Dialog
          open={true}
          onClose={close}
          maxWidth={"lg"}
          fullWidth={true}
          aria-labelledby="form-dialog-title"
      >
        <StyledDialogTitle id="form-dialog-title">{title}</StyledDialogTitle>
        <Formik
            initialValues={new InvoiceEmailModel(defaultInvoiceEmailData)}
            enableReinitialize={true}
            validateOnMount={false}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={(values, actions) => {
              actions.setSubmitting(true);
              onSubmit(values)
              .then(() => {
                notifications.notify({
                  level: "success",
                  message: translations.invoiceEmailModalForm.emailSent,
                });
              })
              .finally(() => {
                actions.setSubmitting(false);
                close();
              });
            }}
            validationSchema={invoiceEmailSchema}
        >
          {(props: FormikProps<InvoiceEmailModel>) => {
            const {
              values,
              handleBlur,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
              touched,
              errors,
            } = props;
            return (
              <Form>
                <DialogContent>
                  <Grid
                      container
                      justifyContent="space-around"
                      direction="column"
                      spacing={3}
                  >
                    <StyledGrid size="grow">
                      <SubjectInput
                          id="subject"
                          name="subject"
                          value={values.subject}
                          error={Boolean(errors.subject)}
                          helperText={errors.subject}
                          onChange={(value) => setFieldValue("subject", value)}
                          onBlur={handleBlur}
                      />
                    </StyledGrid>
                    <StyledGrid size="grow">
                      <TazEditor
                          id="htmlText"
                          name="htmlText"
                          value={values.htmlText}
                          onChange={async (value) => {
                            let content = value.trim();
                            if (value.trim() === '<p><br></p>') {
                              content ='';
                            }
                            await setFieldValue("htmlText", content);
                            await setFieldTouched("htmlText", true, true);
                          }}
                          onImportantToggle={async (isImportant) => {
                            await setFieldValue("important", isImportant);
                          }}
                      />
                      {errors.htmlText && (
                          <FormHelperText error>{errors.htmlText}</FormHelperText>
                      )}
                    </StyledGrid>
                    <Grid size="grow">
                      <ClientEmailSelect
                          id="recipients-select"
                          name="recipients-select"
                          values={values.recipients}
                          clientId={clientId}
                          errorMessage={errors.recipients}
                          touched={touched.recipients}
                          onValueChange={(emails) =>
                              setFieldValue("recipients", emails)
                          }
                      />
                    </Grid>

                    <Grid size="grow">
                      <FileSelector
                          id="attachments"
                          name="attachments"
                          value={values.attachments}
                          onChange={setFieldValue}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button type="button" onClick={() => setPreview(true)}>
                    {translations.invoiceEmailModalForm.buttons.previewInvoice}
                  </Button>
                  <Button
                      type="button"
                      className="outline"
                      onClick={close}
                      disabled={isSubmitting}
                  >
                    {translations.invoiceEmailModalForm.buttons.close}
                  </Button>
                  <Button
                      type="submit"
                      disabled={isSubmitting}
                      variant="contained"
                      color="primary"
                  >
                    {translations.invoiceEmailModalForm.buttons.send}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
      {preview && (
          <InvoiceModalView close={() => setPreview(false)} pdfUrl={pdfUrl}/>
      )}
    </RootContainer>
  );
};

export default InvoiceEmailModalForm;
